import axios from '@common/axios';

/**
 * Service for fetching lesson resources from the backend API
 */
class LessonResourceService {
    /**
     * Get summary counts of all resource types for a concept
     * @param {number} conceptId - The concept ID
     * @returns {Promise<Object>} Resource summary with counts by category
     */
    async getResourceSummary(conceptId) {
        const response = await axios.get(`/web/ui/lessonResources/concept/${conceptId}/summary`);
        return response.data;
    }

    /**
     * Get media resources for a specific type and concept
     * @param {number} conceptId - The concept ID
     * @param {string} mediaType - Media type (VIDEO, WORKSHEET, WIDGET, etc.) - optional
     * @param {number} page - Page number (0-based, default 0)
     * @param {number} pageSize - Items per page (default 50)
     * @returns {Promise<PageInfo>} Paginated media resources
     */
    async getMediaResources(conceptId, mediaType = null, page = 0, pageSize = 50) {
        const params = { page, pageSize };
        if (mediaType) {
            params.mediaType = mediaType;
        }
        const response = await axios.get(
            `/web/ui/lessonResources/concept/${conceptId}/media`,
            { params }
        );
        return response.data;
    }

    /**
     * Get question resources summary for a specific type and concept
     * @param {number} conceptId - The concept ID
     * @param {string} questionType - Question type (WALKTHROUGH, PRACTICE, TEST_ONLY, EXAM, DIAGNOSTIC)
     * @param {Array<string>} questionSubTypes - Optional array of question subtypes to filter by
     * @param {number} limit - Maximum number of results (default 50)
     * @returns {Promise<Object>} Question resource summary
     */
    async getQuestionResources(conceptId, questionType, questionSubTypes = null, limit = 50) {
        const params = { questionType, limit };
        if (questionSubTypes && questionSubTypes.length > 0) {
            params.questionSubTypes = questionSubTypes;
        }
        const response = await axios.get(
            `/web/ui/lessonResources/concept/${conceptId}/questions`,
            { params }
        );
        return response.data;
    }

    /**
     * Get question clusters for a concept
     * @param {number} conceptId - The concept ID
     * @param {Array<string>} questionClusterTypes - Optional array of cluster types to filter by
     * @param {Array<string>} questionClusterSubTypes - Optional array of cluster subtypes to filter by
     * @param {number} page - Page number (0-based, default 0)
     * @param {number} pageSize - Items per page (default 50)
     * @returns {Promise<PageInfo>} Paginated question clusters
     */
    async getQuestionClusters(conceptId, questionClusterTypes = null, questionClusterSubTypes = null, page = 0, pageSize = 50) {
        const params = { page, pageSize };
        if (questionClusterTypes && questionClusterTypes.length > 0) {
            params.questionClusterTypes = questionClusterTypes;
        }
        if (questionClusterSubTypes && questionClusterSubTypes.length > 0) {
            params.questionClusterSubTypes = questionClusterSubTypes;
        }
        const response = await axios.get(
            `/web/ui/lessonResources/concept/${conceptId}/questionClusters`,
            { params }
        );
        return response.data;
    }

    /**
     * Get tutorials for a specific type and concept
     * @param {number} conceptId - The concept ID
     * @param {string} tutorialType - Tutorial type (LESSON, EXERCISE) - optional
     * @param {Array<string>} tutorialSubTypes - Optional array of tutorial subtypes to filter by
     * @param {number} page - Page number (0-based, default 0)
     * @param {number} pageSize - Items per page (default 50)
     * @returns {Promise<PageInfo>} Paginated tutorials
     */
    async getTutorials(conceptId, tutorialType = null, tutorialSubTypes = null, page = 0, pageSize = 50) {
        const params = { page, pageSize };
        if (tutorialType) {
            params.tutorialType = tutorialType;
        }
        if (tutorialSubTypes && tutorialSubTypes.length > 0) {
            params.tutorialSubTypes = tutorialSubTypes;
        }
        const response = await axios.get(
            `/web/ui/lessonResources/concept/${conceptId}/tutorials`,
            { params }
        );
        return response.data;
    }

    /**
     * Get practice questions for a concept
     * @param {number} conceptId - The concept ID
     * @param {Array<string>} questionSubTypes - Optional array of question subtypes to filter by
     * @param {number} page - Page number (0-based, default 0)
     * @param {number} pageSize - Items per page (default 50)
     * @returns {Promise<PageInfo>} Paginated practice questions
     */
    async getPracticeQuestions(conceptId, questionSubTypes = null, page = 0, pageSize = 50) {
        const params = { page, pageSize };
        if (questionSubTypes && questionSubTypes.length > 0) {
            params.questionSubTypes = questionSubTypes;
        }
        const response = await axios.get(
            `/web/ui/lessonResources/concept/${conceptId}/practiceQuestions`,
            { params }
        );
        return response.data;
    }

    /**
     * Get walkthrough questions for a concept
     * @param {number} conceptId - The concept ID
     * @param {number} page - Page number (0-based, default 0)
     * @param {number} pageSize - Items per page (default 50)
     * @returns {Promise<PageInfo>} Paginated walkthrough questions
     */
    async getWalkthroughQuestions(conceptId, page = 0, pageSize = 50) {
        const response = await axios.get(
            `/web/ui/lessonResources/concept/${conceptId}/walkthroughQuestions`,
            { params: { page, pageSize } }
        );
        return response.data;
    }

    /**
     * Get exercise questions for a concept
     * Exercise questions are manually-marked practice questions in question clusters
     * @param {number} conceptId - The concept ID
     * @param {number} page - Page number (0-based, default 0)
     * @param {number} pageSize - Items per page (default 50)
     * @returns {Promise<PageInfo>} Paginated exercise questions
     */
    async getExerciseQuestions(conceptId, page = 0, pageSize = 50) {
        const response = await axios.get(
            `/web/ui/lessonResources/concept/${conceptId}/exerciseQuestions`,
            { params: { page, pageSize } }
        );
        return response.data;
    }

    /**
     * Get quiz questions for a concept
     * Quiz questions are auto-marked practice questions in question clusters
     * @param {number} conceptId - The concept ID
     * @param {number} page - Page number (0-based, default 0)
     * @param {number} pageSize - Items per page (default 50)
     * @returns {Promise<PageInfo>} Paginated quiz questions
     */
    async getQuizQuestions(conceptId, page = 0, pageSize = 50) {
        const response = await axios.get(
            `/web/ui/lessonResources/concept/${conceptId}/quizQuestions`,
            { params: { page, pageSize } }
        );
        return response.data;
    }

    /**
     * Search resources with flexible filtering
     * @param {number} conceptId - The concept ID
     * @param {Object} searchParams - Search parameters
     * @param {Array<string>} searchParams.resourceTypes - Filter by resource types (MEDIA, QUESTION, TUTORIAL, QUESTION_CLUSTER)
     * @param {Array<string>} searchParams.mediaTypes - Filter by media types
     * @param {Array<string>} searchParams.questionTypes - Filter by question types
     * @param {Array<string>} searchParams.tutorialTypes - Filter by tutorial types
     * @param {Array<string>} searchParams.questionClusterTypes - Filter by question cluster types
     * @param {string} searchParams.instructionalPhase - Filter by instructional phase (HOOK, EXPLAIN, MODEL, PRACTICE, ASSESS, REVIEW)
     * @param {string} searchParams.pedagogicalRole - Filter by pedagogical role
     * @param {string} searchParams.dokLevel - Filter by DOK level (LEVEL_1, LEVEL_2, LEVEL_3, LEVEL_4)
     * @param {string} searchParams.cognitiveLabel - Filter by cognitive label
     * @param {string} searchParams.knowledgeDimension - Filter by knowledge dimension
     * @param {string} searchParams.lessonGoalCategory - Filter by lesson goal category
     * @param {number} searchParams.minEstimatedTime - Minimum estimated time in minutes
     * @param {number} searchParams.maxEstimatedTime - Maximum estimated time in minutes
     * @param {number} searchParams.minEngagementScore - Minimum engagement score (0-100)
     * @param {number} searchParams.maxEngagementScore - Maximum engagement score (0-100)
     * @param {string} searchParams.searchText - Text search in name, description
     * @param {number} searchParams.page - Page number (0-based, default 0)
     * @param {number} searchParams.pageSize - Items per page (default 50, max 100)
     * @param {string} searchParams.sortBy - Field to sort by (name, estimatedTime, engagementScore, dokLevel)
     * @param {string} searchParams.sortDirection - Sort direction (ASC or DESC)
     * @returns {Promise<Object>} Paginated search results
     */
    async searchResources(conceptId, searchParams = {}) {
        const response = await axios.post(
            `/web/ui/lessonResources/concept/${conceptId}/search`,
            {
                conceptId,
                ...searchParams
            }
        );
        return response.data;
    }
}

export default new LessonResourceService();
